import { COLOR_CODES_MEN, COLOR_CODES_WOMEN } from '@/components/rangePlan/constants';
import { checkUserType, isAdmin, isFashionDesigner } from '@/utils/Util';

export const checkValidId = (item) => {
	if (typeof item?.id === 'string' && item?.id?.includes('INDEX_')) {
		return null;
	} else {
		return item?.id;
	}
};

export const organizeResponse = (response, key = 'name') => {
	return response?.map((item) => ({
		...item,
		value: item?.id,
		label: item?.[key]
	}));
};

export const validatePopupRequiredFields = (values, fieldName, setErrors) => {
	const newErrors = {};
	if (!values.min || !values.max) {
		newErrors.min = `${fieldName} is required`;
		newErrors.max = `${fieldName} is required`;
	}
	if (parseFloat(values.min) > parseFloat(values.max)) {
		newErrors.min = `${fieldName} should be less than max ${fieldName}`;
	}
	setErrors(newErrors);
	return Object.keys(newErrors).length > 0;
};

export const getRangeValue = (min, max, isPrice = false) => {
	if (min && max) {
		return `${isPrice ? '$' : ''}${isPrice ? min.toFixed(2) : min} - ${isPrice ? '$' : ''}${
			isPrice ? max.toFixed(2) : max
		}`;
	} else if (min) {
		return `${isPrice ? '$' : ''}${isPrice ? min.toFixed(2) : min}`;
	} else if (max) {
		return `${isPrice ? '$' : ''}${isPrice ? max.toFixed(2) : max}`;
	}
	return '';
};

export const isAllowedToDelete = (item) =>
	!item?.achievedMaxGsm &&
	!item?.achievedMaxPrice &&
	!item?.achievedMinGsm &&
	!item?.achievedMinPrice &&
	!item?.achievedQuantity &&
	Object.keys(item?.achievedCompositionDetailsCountMap ?? {})?.length === 0 &&
	Object.keys(item?.achievedConstructionCountMap ?? {})?.length === 0 &&
	Object.keys(item?.achievedFitTypeCountMap ?? {})?.length === 0;

export const isAllowedToUpdateTarget = (item) =>
	item?.fabricType?.name !== 'NA' ||
	item?.parentCategory?.name !== 'NA' ||
	item?.categoryResponse?.name !== 'NA' ||
	item?.productGroupResponse?.name !== 'NA';

export const isCellIdMatched = (itemId, item) => {
	return item?.id === itemId && isAllowedToUpdateTarget(item);
};

export const groupByField = (field, rangePlanList) => {
	const groupedData = {};
	let currentId = null;
	let currentSum = 0;

	const getFieldInfo = (item) => {
		switch (field) {
			case 'parentCategory':
				return {
					isNew: item?.isNewParentCategory,
					id: item?.parentCategoryId
				};
			case 'fabricType':
				return {
					isNew: item?.isNewFabricType,
					id: item?.fabricTypeId
				};
			case 'productGroup':
				return {
					isNew: item?.isNewProductGroup,
					id: item?.mainProductGroupId
				};
			default:
				return {};
		}
	};

	rangePlanList?.forEach((item) => {
		const { isNew, id } = getFieldInfo(item);

		if (isNew) {
			if (currentId) {
				groupedData[currentId] = currentSum;
			}

			currentId = id;
			currentSum = Number(item?.achievedQuantity || 0);
		} else {
			currentSum += Number(item?.achievedQuantity || 0);
		}
	});

	if (currentId) {
		groupedData[currentId] = currentSum;
	}

	return groupedData;
};

export const calculateStylesCount = (rangePlanList = []) => {
	let costedStyles = 0;
	let inRangeStyles = 0;
	let outOfRangeStyles = 0;
	let sampleDevelopedStyles = 0;
	let inConsiderationStyles = 0;
	let notPlannedTpStyles = 0;

	if (!Array.isArray(rangePlanList)) {
		return { costedStyles, inRangeStyles, outOfRangeStyles, notPlannedTpStyles };
	}

	rangePlanList.forEach((item) => {
		if (!item) return;
		const totalCosted = Number(item?.totalCostedQuantity) || 0;
		const inRange = Number(item?.inRangeCount) || 0;

		if (totalCosted > 0) {
			costedStyles += totalCosted;
			inRangeStyles += inRange;
		}

		if (item?.sampleDevelopedQuantity > 0) {
			sampleDevelopedStyles += item?.sampleDevelopedQuantity;
		}

		if (item?.inConsiderationCount > 0) {
			inConsiderationStyles += item?.inConsiderationCount;
		}

		if (item?.achievedQuantity && !item?.targetQuantity) {
			notPlannedTpStyles += item?.achievedQuantity;
		}

		outOfRangeStyles += item?.outOfRangeCount || 0;
	});

	return {
		costedStyles: costedStyles,
		inRangeStyles: inRangeStyles,
		outOfRangeStyles: outOfRangeStyles,
		sampleDevelopedStyles: sampleDevelopedStyles,
		inConsiderationStyles: inConsiderationStyles,
		notPlannedTpStyles: notPlannedTpStyles
	};
};

export const getReducedPrice = (value, percentage = 10) => {
	if (!value) return '';
	const reducedAmount = (parseFloat(value) * percentage) / 100;
	return value - reducedAmount;
};

export const getBackgroundColor = (item) => {
	const groupName = item?.productGroupResponse?.name;
	const fabricType = item?.fabricType?.constant;

	if (groupName === 'Men') {
		if (fabricType === 'KNIT') return COLOR_CODES_MEN.knit;
		if (fabricType === 'WOVEN') return COLOR_CODES_MEN.woven;
		if (fabricType === 'DENIM') return COLOR_CODES_MEN.denim;
	} else if (groupName === 'Women') {
		if (fabricType === 'KNIT') return COLOR_CODES_WOMEN.knit;
		if (fabricType === 'WOVEN') return COLOR_CODES_WOMEN.woven;
		if (fabricType === 'DENIM') return COLOR_CODES_WOMEN.denim;
	}
	return '#fff';
};

export const getBackgroundColorForSummaryTable = (groupName) => {
	// Men fabric types
	if (groupName.includes('Men-Knit')) return COLOR_CODES_MEN.knit;
	if (groupName.includes('Men-Woven')) return COLOR_CODES_MEN.woven;
	if (groupName.includes('Men-Denim')) return COLOR_CODES_MEN.denim;

	// Women fabric types
	if (groupName.includes('Women-Knit')) return COLOR_CODES_WOMEN.knit;
	if (groupName.includes('Women-Woven')) return COLOR_CODES_WOMEN.woven;
	if (groupName.includes('Women-Denim')) return COLOR_CODES_WOMEN.denim;

	return '#fff'; // Default
};

export const getUniqueAndCalculateSums = (data) => {
	const departmentSums = {};
	const fabricTypeSums = {};
	const parentCategorySums = {};

	data?.forEach((item) => {
		const department = item?.productGroupResponse?.name;
		const fabricType = item?.fabricType?.name;
		const fabricTypeConstant = item?.fabricType?.constant || item?.fabricType?.name;
		const parentCategory = item?.parentCategory?.name;
		const parentCategoryConstant = item?.parentCategory?.constant;
		const sampleQuantity = item?.achievedQuantity || 0;

		// Add to department totals
		if (!departmentSums[department] && department) {
			departmentSums[department] = 0;
		}
		if (department) {
			departmentSums[department] += sampleQuantity;
		}

		// Add to fabric type totals
		if (!fabricTypeSums[fabricTypeConstant] && fabricTypeConstant) {
			fabricTypeSums[fabricTypeConstant] = { name: fabricType, total: 0 };
		}
		if (fabricTypeConstant) {
			fabricTypeSums[fabricTypeConstant].total += sampleQuantity;
		}

		// Add to parent Category total
		if (!parentCategorySums[parentCategoryConstant] && parentCategoryConstant) {
			parentCategorySums[parentCategoryConstant] = { name: parentCategory, total: 0 };
		}
		if (parentCategoryConstant) {
			parentCategorySums[parentCategoryConstant].total += sampleQuantity;
		}
	});

	return { departmentSums, fabricTypeSums, parentCategorySums };
};

// Group data by productGroupResponse and calculate totals (Summary Table)
export const getProductGroupTotals = (data) => {
	const groupedData = {};

	data?.forEach((item) => {
		const productGroup = item?.productGroupResponse?.name;
		const fabricType = item?.fabricType?.name;
		if (!productGroup || !fabricType) return;

		const groupKey = `${productGroup}-${fabricType}`;

		if (!groupedData[groupKey]) {
			groupedData[groupKey] = {
				name: groupKey,
				targetQuantity: 0,
				achievedQuantity: 0,
				sampleDevelopedQuantity: 0,
				totalCostedQuantity: 0,
				items: [],
				inRangeCount: 0,
				inConsiderationCount: 0,
				outOfRangeCount: 0,
				productSelectedQuantity: 0,
				productPresentedQuantity: 0
			};
		}

		groupedData[groupKey].targetQuantity += Number(item.targetQuantity || 0);
		groupedData[groupKey].achievedQuantity += Number(item.achievedQuantity || 0);
		groupedData[groupKey].sampleDevelopedQuantity += Number(item.sampleDevelopedQuantity || 0);
		groupedData[groupKey].totalCostedQuantity += Number(item.totalCostedQuantity || 0);
		groupedData[groupKey].items.push(item);
		groupedData[groupKey].inRangeCount += Number(item.inRangeCount || 0);
		groupedData[groupKey].inConsiderationCount += Number(item.inConsiderationCount || 0);
		groupedData[groupKey].outOfRangeCount += Number(item.outOfRangeCount || 0);
		groupedData[groupKey].productSelectedQuantity += Number(item.productSelectedQuantity || 0);
		groupedData[groupKey].productPresentedQuantity += Number(item.productPresentedQuantity || 0);
	});

	return groupedData;
};

export const getFabricTypeWiseParentGroupCount = (rangePlanList) => {
	const groupedData = {};

	rangePlanList?.forEach((item) => {
		const productGroupId = item?.productGroupResponse?.id;
		const fabricTypeId = item?.fabricType?.constant || item?.fabricType?.name;
		if (!productGroupId || !fabricTypeId) return;

		const key = `${productGroupId}_${fabricTypeId}`;
		if (!groupedData[key]) {
			groupedData[key] = 0;
		}

		groupedData[key] += Number(item?.achievedQuantity || 0);
	});

	return groupedData;
};

export const hasUserEditPermission = () => {
	return (
		isAdmin() ||
		isFashionDesigner() ||
		checkUserType('ACCOUNT_MANAGER') ||
		checkUserType('BUSINESS_DEVELOPMENT_MANAGER')
	);
};
